import React, { Suspense, lazy, useEffect } from "react";
import { Navigate, Route, Routes, Link } from "react-router-dom";
import { useWallet } from "@solana/wallet-adapter-react";

import { LobbyScene } from "./scene/Lobby";
import { WatchPage } from "./watch/WatchPage";
import { ChatPanel } from "./chat";
import { DevConsole } from "./dev/DevConsole";
import { LaunchProvider } from "./launch/LaunchProvider";
import { useTrack } from "./launch/useTrack";
import { TokenMcapPanel } from "./token/TokenMcapPanel";
import { MilestoneToast } from "./token/MilestoneToast";
import { installMilestoneLogBridge } from "./token/milestoneEvents";
import { useFlywheelOnChainBridge } from "./token/useFlywheelOnChain";
import { IosWalletDeeplinkBanner } from "./wallet/IosWalletDeeplinkBanner";
import { WalletButton } from "./wallet/WalletButton";
import { DraggableOverlay } from "./util/DraggableOverlay";
import { useIsTouch } from "./util/useIsTouch";

installMilestoneLogBridge();

// Lazy routes so the build works even before Lane F and Lane I land.
// The dynamic specifiers below resolve to Lane F (arena) and Lane I (explorer)
// once those lanes merge. Until then the import() rejects and we fall back
// to a placeholder. @ts-expect-error covers the missing module path.
const ArenaScene = lazy(async () => {
  const mod = await import(
    /* @vite-ignore */
    "./games/arena/ArenaScene"
  ).catch(() => ({ default: MissingLanePlaceholder("F", "Coin Arena") }));
  return { default: (mod as { default: React.ComponentType }).default };
});

const ExplorerPage = lazy(async () => {
  const mod = await import("./explorer/ExplorerPage").catch(() => ({
    default: MissingLanePlaceholder("I", "Session Explorer"),
  }));
  return { default: (mod as { default: React.ComponentType }).default };
});

// Admin ops dashboard (read-only SOL-inflow + season health, admin-gated).
const OpsDashboard = lazy(() => import("./ops/OpsDashboard"));

function MissingLanePlaceholder(
  lane: string,
  label: string,
): React.ComponentType {
  const Placeholder: React.FC = () => (
    <div style={pageStyle}>
      <h2>{label}</h2>
      <p>
        Lane {lane} has not been merged yet. This route will activate once it
        ships.
      </p>
      <Link to="/">Back to Lobby</Link>
    </div>
  );
  return Placeholder;
}

const pageStyle: React.CSSProperties = {
  padding: 24,
  height: "100%",
  display: "flex",
  flexDirection: "column",
  gap: 12,
};

const navLinkStyle: React.CSSProperties = {
  padding: "6px 10px",
  background: "#1a1a24",
  border: "1px solid #2a2a36",
  borderRadius: 6,
  color: "#e8e8ef",
  textDecoration: "none",
  fontSize: 13,
};

/** Top-right HUD overlay: wallet button + nav. Draggable on touch so the
 *  player can move it out of the way of in-canvas elements. */
function HudOverlay(): JSX.Element {
  const isTouch = useIsTouch();
  return (
    <DraggableOverlay
      id="hud-overlay"
      defaultAnchor={{ top: 12, right: 12, safeAreaTop: isTouch }}
      enabled={isTouch}
      zIndex={10}
      nonDragSelector="a, button, [data-no-drag]"
    >
      <div
        style={{
          display: "flex",
          gap: 8,
          alignItems: "center",
        }}
      >
        <Link to="/explorer" style={navLinkStyle}>
          Explorer
        </Link>
        <WalletButton />
      </div>
    </DraggableOverlay>
  );
}

/** Funnel beacon. Lives under <LaunchProvider> so useTrack sees the right
 *  launch slug. Fires `landing` on first mount and `wallet_connect` the
 *  moment the wallet adapter reports a public key. Rendering nothing. */
function FunnelTracker(): null {
  const { track } = useTrack();
  const { publicKey } = useWallet();
  useEffect(() => {
    track("landing");
  }, [track]);
  useEffect(() => {
    if (!publicKey) return;
    track("wallet_connect", { wallet: publicKey.toBase58() });
  }, [publicKey, track]);
  return null;
}

export function App(): JSX.Element {
  useFlywheelOnChainBridge();
  return (
    <LaunchProvider>
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      <FunnelTracker />
      <DevConsole />
      <MilestoneToast />
      <IosWalletDeeplinkBanner />
      <Routes>
        <Route
          path="/"
          element={
            <>
              <LobbyScene />
              <TokenMcapPanel />
              <HudOverlay />
              <ChatPanel />
            </>
          }
        />
        <Route
          path="/arena"
          element={
            <Suspense fallback={<LoadingPanel label="Loading Coin Arena…" />}>
              <ArenaScene />
              <TokenMcapPanel />
              <HudOverlay />
            </Suspense>
          }
        />
        {/* Unattended broadcast view — spectator + director cam + broadcast HUD.
            The surface OBS captures for narration and the auto-stream box loads. */}
        <Route path="/watch" element={<WatchPage />} />
        {/* Last Touch lives inside the main lobby now — redirect any old links. */}
        <Route path="/last-touch" element={<Navigate to="/" replace />} />
        <Route
          path="/explorer"
          element={
            <Suspense fallback={<LoadingPanel label="Loading Explorer…" />}>
              <ExplorerPage />
              <HudOverlay />
            </Suspense>
          }
        />
        <Route
          path="/explorer/:sessionId"
          element={
            <Suspense fallback={<LoadingPanel label="Loading Session…" />}>
              <ExplorerPage />
              <HudOverlay />
            </Suspense>
          }
        />
        <Route
          path="/ops"
          element={
            <Suspense fallback={<LoadingPanel label="Loading Ops…" />}>
              <OpsDashboard />
            </Suspense>
          }
        />
      </Routes>
    </div>
    </LaunchProvider>
  );
}

function LoadingPanel({ label }: { label: string }): JSX.Element {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100%",
        color: "#9aa0b4",
        fontSize: 14,
      }}
    >
      {label}
    </div>
  );
}
